import React, { useCallback, useMemo, useState } from 'react';
import { Card, Stack, Flex, ActionIcon, Text, Paper, Space, Group, Button, Tooltip, Title } from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconPencil, IconTrash } from '@tabler/icons';
import FlipMove from 'react-flip-move';
import FloatingLabelInput from '../Components/Input';
import useRingColors from '../Hooks/use-ring-colors';
import clone from 'clone';
import { NavLink } from 'react-router-dom';
import { Entity } from '../../backend/models/entity';

type PropType = {
  initial: Entity[];
  updateEntities(created: Entity[], deleted: Entity[], updated: Entity[]): Promise<Response>;
  getEntities(): Promise<Entity[]>;
  heading: string;
  textAboveList: string;
  textBelowList?: string;
  label: string;
  placeholder: string;
  maxEntities: number;
  editUrlPath: string;
  isRings?: boolean;
  loading: boolean;
  setLoading: (loading: boolean) => void;
};

const EntityManagement = (props: PropType) => {
  const [initialEntities, setInitialEntities] = useState<Entity[]>(props.initial);
  const [entities, setEntities] = useState<Entity[]>([...clone(initialEntities)]);
  const [newEntityName, setNewEntityName] = useState<string>('');

  const { ringColors } = useRingColors();
  const [loading, setLoading] = useState<boolean>(false);

  const shiftUp = useCallback((i: number) => {
    setEntities((entities) => {
      let data = [...entities];
      let temp = data[i];
      data[i] = data[i - 1];
      data[i - 1] = temp;
      return data;
    });
  }, []);

  const shiftDown = useCallback((i: number) => {
    setEntities((entities) => {
      let data = [...entities];
      let temp = data[i];
      data[i] = data[i + 1];
      data[i + 1] = temp;
      return data;
    });
  }, []);

  function remove(name: string) {
    const arr = entities.filter((e) => e.name !== name);
    setEntities([...arr]);
  }

  function add() {
    setEntities((entities) => [...entities, { name: newEntityName.trim(), order: entities.length, id: undefined }]);
    setNewEntityName('');
  }

  const nameExists = useMemo(() => {
    return props.initial.map((e) => e.name).includes(newEntityName.trim());
  }, [newEntityName, props.initial]);

  const unchanged = useMemo(() => {
    return (
      initialEntities.length === entities.length && initialEntities.every((entity, i) => entity.id === entities[i].id)
    );
  }, [initialEntities, entities]);

  async function onSubmit() {
    setLoading(true);
    entities.map((e, i) => (e.order = i));
    const created = entities.filter((e) => e.id === undefined);
    const deleted = initialEntities.filter((ie) => !entities.find((e) => ie.id === e.id));
    const updated = entities.filter((e, i) => {
      if (e.id === undefined) {
        return false;
      }
      const initial = initialEntities.find((ie) => ie.id === e.id);
      return initial?.order !== e.order || initial?.name !== e.name;
    });
    await props.updateEntities(created, deleted, updated);
    props.getEntities().then((res) => {
      setInitialEntities(clone(res));
      setEntities(clone(res));
      setLoading(false);
    });
  }

  return (
    <Card withBorder shadow="sm" w={600} maw={'100%'}>
      <Stack spacing={'xs'}>
        <Group align={'baseline'}>
          <Title order={2}>{props.heading}</Title>
          <Text fz="xs" c="dimmed">
            {`(max ${props.maxEntities})`}
          </Text>
        </Group>
        <Text fz="sm" c="dimmed">
          {props.textAboveList}
        </Text>
        <Group align={'center'} grow>
          <>
            {props.isRings && entities.length > 0 && (
              <Stack spacing={0} sx={{ flexGrow: 0 }}>
                {entities.map((entity, i) => {
                  return (
                    <Paper
                      withBorder
                      py={8}
                      px={'lg'}
                      mb={i === entities.length - 1 ? 0 : 'md'}
                      style={{ backgroundColor: `${ringColors[i]}`, height: 78 }}
                      key={entity.name + '-color'}
                    />
                  );
                })}
              </Stack>
            )}
            <FlipMove>
              {entities.map((entity, i) => {
                return (
                  <Paper
                    withBorder
                    shadow={'sm'}
                    py={8}
                    px={'lg'}
                    mb={i === entities.length - 1 ? 0 : 'md'}
                    key={entity.name}
                  >
                    <Flex
                      justify={'space-between'}
                      align={'center'}
                      style={{
                        width: '100%',
                      }}
                    >
                      <Text fw={500}>{entity.name}</Text>
                      <Group>
                        <Tooltip label="Edit">
                          <NavLink to={`${props.editUrlPath}/${entity.name.toLocaleLowerCase()}`}>
                            <ActionIcon variant="subtle">
                              <IconPencil size={24} />
                            </ActionIcon>
                          </NavLink>
                        </Tooltip>
                        <Tooltip label="Remove">
                          <ActionIcon variant="subtle" onClick={() => remove(entity.name)}>
                            <IconTrash size={24} />
                          </ActionIcon>
                        </Tooltip>
                        <Stack spacing={4}>
                          <Tooltip label="Move up">
                            <ActionIcon variant="subtle" disabled={i === 0} onClick={() => shiftUp(i)}>
                              <IconChevronUp />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label="Move down">
                            <ActionIcon
                              variant="subtle"
                              disabled={i === entities.length - 1}
                              onClick={() => shiftDown(i)}
                            >
                              <IconChevronDown />
                            </ActionIcon>
                          </Tooltip>
                        </Stack>
                      </Group>
                    </Flex>
                  </Paper>
                );
              })}
            </FlipMove>
          </>
        </Group>
        <Text fz="sm" c="dimmed">
          {props.textBelowList}
        </Text>
      </Stack>
      {entities.length < props.maxEntities && (
        <>
          <Space h={'md'} />
          <Group grow align={'flex-end'}>
            <FloatingLabelInput
              label={props.label}
              placeholder={props.placeholder}
              value={newEntityName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewEntityName(e.currentTarget.value)}
            />
            <Button onClick={() => add()} disabled={newEntityName.trim().length === 0 || nameExists}>
              Add
            </Button>
          </Group>
          {nameExists && (
            <Text mt={'xs'} color={'red'}>{`There is already a ${
              props.isRings ? 'ring' : 'section'
            } with the name ${newEntityName.trim()}!`}</Text>
          )}
        </>
      )}
      <Space h={'md'}></Space>
      <Group position="right">
        <Button disabled={unchanged} onClick={onSubmit} loading={loading}>
          Save
        </Button>
      </Group>
    </Card>
  );
};

export default EntityManagement;
